import { addDays } from '../calendar/dates.js';
import type { HolidayRegion } from '../calendar/holidays.js';
import { addWorkingDays, nearestWorkingDay } from '../calendar/working-days.js';
import { workingDuration, type DateRange } from './constraints.js';

// Date rules for dragging bars in the Gantt (GANTT-03, GANTT-07, decided in spike 6a). The
// frontend computes the preview with these; the API stores what it receives.

export type DragMode = 'move' | 'resize-start' | 'resize-end';

/**
 * Applies a drag of `deltaDays` calendar days. Moving keeps the duration in working days and
 * snaps the start to the nearest working day in drag direction. Resizing snaps the dragged edge
 * to a working day and never crosses the other edge. Milestones only move.
 */
export function dragRange(
  range: DateRange,
  mode: DragMode,
  deltaDays: number,
  region: HolidayRegion,
  milestone = false,
): DateRange {
  if (deltaDays === 0) return range;
  const direction = deltaDays > 0 ? 'forward' : 'backward';
  if (milestone || mode === 'move') {
    const startDate = nearestWorkingDay(addDays(range.startDate, deltaDays), region, direction);
    if (milestone) return { startDate, dueDate: startDate };
    const span = workingDuration(range, region) - 1;
    return { startDate, dueDate: addWorkingDays(startDate, span, region) };
  }
  if (mode === 'resize-start') {
    const startDate = nearestWorkingDay(addDays(range.startDate, deltaDays), region, direction);
    return {
      startDate: startDate > range.dueDate ? range.dueDate : startDate,
      dueDate: range.dueDate,
    };
  }
  const dueDate = nearestWorkingDay(addDays(range.dueDate, deltaDays), region, direction);
  return {
    startDate: range.startDate,
    dueDate: dueDate < range.startDate ? range.startDate : dueDate,
  };
}

/** Default length of a task dragged from „Nicht terminiert“ into the plan (GANTT-09). */
export const DROP_DURATION_WORKING_DAYS = 5;

/** Range of an unscheduled task dropped onto `date` (milestones: that working day). */
export function rangeFromDrop(date: string, region: HolidayRegion, milestone = false): DateRange {
  const startDate = nearestWorkingDay(date, region);
  if (milestone) return { startDate, dueDate: startDate };
  return {
    startDate,
    dueDate: addWorkingDays(startDate, DROP_DURATION_WORKING_DAYS - 1, region),
  };
}
